import { useEffect } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'
import { Header } from './components/Header'
import { ToastProvider } from './components/Toast'
import { ErrorBoundary } from './components/ErrorBoundary'
import { DemoBanner } from './components/DemoBanner'
import { CommandPalette } from './components/CommandPalette'
import { AuthProvider, useAuth } from './lib/auth'
import { LoginPage } from './pages/Login'
import { AuditPage } from './pages/Audit'
import { DashboardPage } from './pages/Dashboard'
import { ProductionOrdersPage } from './pages/ProductionOrders'
import { WorkOrdersPage } from './pages/WorkOrders'
import { EquipmentDataPage } from './pages/EquipmentData'
import { InspectionPage } from './pages/Inspection'
import { EquipmentStatusPage } from './pages/EquipmentStatus'
import { LotTracePage } from './pages/LotTrace'
import { SpcPage } from './pages/Spc'
import { AndonPage } from './pages/Andon'
import { MaintenancePage } from './pages/Maintenance'
import { LaborPage } from './pages/Labor'
import { BomsPage } from './pages/Boms'
import { SchedulePage } from './pages/Schedule'
import { ForecastingPage } from './pages/Forecasting'
import { CostPage } from './pages/Cost'
import { InventoryPage } from './pages/Inventory'
import { AccountPage } from './pages/Account'
import { I18nProvider } from './lib/i18n'
import { useGlobalReveal } from './hooks/useReveal'

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) {
    return (
      <main className="container" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <p className="muted">로딩 중…</p>
      </main>
    )
  }
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />
  return children
}

function Shell() {
  const { user, logout } = useAuth()
  useGlobalReveal()
  useEffect(() => {
    // 페이지 전환 시 reveal 재관찰
    const obs = setTimeout(() => {
      document.querySelectorAll('.reveal:not(.in)').forEach(() => {})
    }, 50)
    return () => clearTimeout(obs)
  })

  return (
    <>
      <DemoBanner />
      <Header isAuthenticated={!!user} onLogout={logout} />
      {user && <CommandPalette />}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
        <Route path="/production-orders" element={<RequireAuth><ProductionOrdersPage /></RequireAuth>} />
        <Route path="/work-orders" element={<RequireAuth><WorkOrdersPage /></RequireAuth>} />
        <Route path="/equipment-data" element={<RequireAuth><EquipmentDataPage /></RequireAuth>} />
        <Route path="/inspection" element={<RequireAuth><InspectionPage /></RequireAuth>} />
        <Route path="/equipment-status" element={<RequireAuth><EquipmentStatusPage /></RequireAuth>} />
        <Route path="/lots" element={<RequireAuth><LotTracePage /></RequireAuth>} />
        <Route path="/spc" element={<RequireAuth><SpcPage /></RequireAuth>} />
        <Route path="/andon" element={<RequireAuth><AndonPage /></RequireAuth>} />
        <Route path="/maintenance" element={<RequireAuth><MaintenancePage /></RequireAuth>} />
        <Route path="/labor" element={<RequireAuth><LaborPage /></RequireAuth>} />
        <Route path="/boms" element={<RequireAuth><BomsPage /></RequireAuth>} />
        <Route path="/schedule" element={<RequireAuth><SchedulePage /></RequireAuth>} />
        <Route path="/forecasting" element={<RequireAuth><ForecastingPage /></RequireAuth>} />
        <Route path="/cost" element={<RequireAuth><CostPage /></RequireAuth>} />
        <Route path="/inventory" element={<RequireAuth><InventoryPage /></RequireAuth>} />
        <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
        <Route path="/audit" element={<RequireAuth><AuditPage /></RequireAuth>} />
        <Route path="*" element={
          <main className="container" style={{ padding: '4rem 1rem' }}>
            <h1>404</h1>
            <p>존재하지 않는 페이지입니다.</p>
          </main>
        } />
      </Routes>
    </>
  )
}

export function App() {
  return (
    <ErrorBoundary>
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <Shell />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </ErrorBoundary>
  )
}
